import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useReducedMotion } from '../hooks/useReducedMotion';
import { useMediaQuery } from '../hooks/useMediaQuery';

/**
 * Three.js Hero Scene — Precision IRDO 3D Logo Scene.
 * Features:
 * - Dynamic Light/Dark Mode color system adaptation (per IRDO Design System).
 * - Dual-axis 3D rotating precision orbital rings & logo animation.
 * - No bottom grid/pane for a clean, editorial look.
 * - Dynamic floating particle stars adapting to theme toggle.
 */
const HeroScene = () => {
  const containerRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();
  const isMobile = useMediaQuery('(max-width: 768px)');

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    if (isMobile || prefersReducedMotion) return;

    let renderer, scene, camera;
    let animationId;
    let mouseX = 0;
    let mouseY = 0;
    let targetRotationX = 0;
    let targetRotationY = 0;
    let scrollOffset = 0;

    const mainGroup = new THREE.Group();
    const logoGroup = new THREE.Group();

    // References for animated materials & objects
    let radarTargetRing;
    let chipCore;
    let outerOrbitRing1;
    let outerOrbitRing2;
    let particleMaterial;
    let logoMaterials = {};

    // Detect dark mode from HTML element class
    let isDarkMode = document.documentElement.classList.contains('dark');

    const getThemeColors = (dark) => ({
      // Main 3D Text & Geometry Colors
      textPrimary: dark ? 0xd9d7d0 : 0x11110f, // Bright metallic in dark mode so it's not too dark
      textSecondary: dark ? 0x3a3933 : 0xbdbab1,
      accent: dark ? 0xf05a3c : 0xe5482e,       // IRDO Accent Color
      chipBase: dark ? 0x1b1b18 : 0xe9e7e1,
      orbitRing1: dark ? 0x706f69 : 0x9a9891,
      orbitRing2: dark ? 0xf05a3c : 0xe5482e,
      particle: dark ? 0xf05a3c : 0xe5482e,
    });

    const init = () => {
      renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: true,
        powerPreference: 'high-performance',
      });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.setSize(container.clientWidth, container.clientHeight);
      renderer.setClearColor(0x000000, 0);
      container.appendChild(renderer.domElement);

      scene = new THREE.Scene();

      // Camera Position
      camera = new THREE.PerspectiveCamera(
        45,
        container.clientWidth / container.clientHeight,
        0.1,
        100
      );
      camera.position.set(0, 0, 11);

      // Studio Lighting
      const ambientLight = new THREE.AmbientLight(0xffffff, 0.85);
      scene.add(ambientLight);

      const mainLight = new THREE.DirectionalLight(0xffffff, 1.4);
      mainLight.position.set(6, 8, 8);
      scene.add(mainLight);

      const accentLight = new THREE.DirectionalLight(0xf05a3c, 0.8);
      accentLight.position.set(-6, -4, 6);
      scene.add(accentLight);

      // Setup Materials according to IRDO System
      const theme = getThemeColors(isDarkMode);

      logoMaterials.primary = new THREE.MeshStandardMaterial({
        color: theme.textPrimary,
        metalness: 0.75,
        roughness: 0.2,
      });

      logoMaterials.accent = new THREE.MeshStandardMaterial({
        color: theme.accent,
        metalness: 0.8,
        roughness: 0.15,
      });

      logoMaterials.darkBase = new THREE.MeshStandardMaterial({
        color: theme.chipBase,
        metalness: 0.85,
        roughness: 0.25,
      });

      // Build Logo & 3D Orbit
      build3DLogo();
      buildOrbitalFX(theme);

      logoGroup.position.set(3.0, 0.0, 0);
      mainGroup.add(logoGroup);

      // Build Particles
      buildGlobalParticles(theme);

      scene.add(mainGroup);

      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('scroll', handleScroll, { passive: true });
      window.addEventListener('resize', handleResize);

      // Listen for theme changes (Light / Dark mode toggle)
      const themeObserver = new MutationObserver(() => {
        const newIsDark = document.documentElement.classList.contains('dark');
        if (newIsDark !== isDarkMode) {
          isDarkMode = newIsDark;
          updateColors(getThemeColors(isDarkMode));
        }
      });

      themeObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class'],
      });

      return () => themeObserver.disconnect();
    };

    const updateColors = (theme) => {
      if (logoMaterials.primary) logoMaterials.primary.color.setHex(theme.textPrimary);
      if (logoMaterials.accent) logoMaterials.accent.color.setHex(theme.accent);
      if (logoMaterials.darkBase) logoMaterials.darkBase.color.setHex(theme.chipBase);
      if (outerOrbitRing1) outerOrbitRing1.material.color.setHex(theme.orbitRing1);
      if (outerOrbitRing2) outerOrbitRing2.material.color.setHex(theme.orbitRing2);
      if (particleMaterial) particleMaterial.color.setHex(theme.particle);
    };

    const build3DLogo = () => {
      const extrudeSettings = {
        depth: 0.35,
        bevelEnabled: true,
        bevelSegments: 3,
        steps: 1,
        bevelSize: 0.04,
        bevelThickness: 0.04,
      };

      const irdoTextGroup = new THREE.Group();

      // ==========================================
      // 1. HURUF "I"
      // ==========================================
      const shapeI = new THREE.Shape();
      shapeI.moveTo(-0.35, -1.2);
      shapeI.lineTo(0.35, -1.2);
      shapeI.lineTo(0.35, 1.2);
      shapeI.lineTo(-0.35, 1.2);
      shapeI.closePath();

      const meshI = new THREE.Mesh(new THREE.ExtrudeGeometry(shapeI, extrudeSettings), logoMaterials.primary);
      meshI.position.x = -2.8;
      irdoTextGroup.add(meshI);

      // ==========================================
      // 2. HURUF "R"
      // ==========================================
      const shapeR = new THREE.Shape();
      shapeR.moveTo(-0.4, -1.2);
      shapeR.lineTo(0.1, -1.2);
      shapeR.lineTo(0.1, -0.15);
      shapeR.lineTo(0.5, -1.2);
      shapeR.lineTo(1.0, -1.2);
      shapeR.lineTo(0.5, -0.05);
      shapeR.absarc(0.2, 0.55, 0.65, -Math.PI / 2, Math.PI / 2, false);
      shapeR.lineTo(-0.4, 1.2);
      shapeR.closePath();

      const holeR = new THREE.Path();
      holeR.moveTo(0.1, 0.25);
      holeR.lineTo(0.25, 0.25);
      holeR.absarc(0.25, 0.55, 0.3, -Math.PI / 2, Math.PI / 2, false);
      holeR.lineTo(0.1, 0.85);
      holeR.closePath();
      shapeR.holes.push(holeR);

      const meshR = new THREE.Mesh(new THREE.ExtrudeGeometry(shapeR, extrudeSettings), logoMaterials.primary);
      meshR.position.x = -1.5;
      irdoTextGroup.add(meshR);

      // ==========================================
      // 3. HURUF "D"
      // ==========================================
      const shapeD = new THREE.Shape();
      shapeD.moveTo(-0.4, -1.2);
      shapeD.lineTo(0.1, -1.2);
      shapeD.absarc(0.1, 0.0, 1.2, -Math.PI / 2, Math.PI / 2, false);
      shapeD.lineTo(-0.4, 1.2);
      shapeD.closePath();

      const holeD = new THREE.Path();
      holeD.moveTo(0.1, -0.65);
      holeD.absarc(0.1, 0.0, 0.65, -Math.PI / 2, Math.PI / 2, false);
      holeD.lineTo(0.1, 0.65);
      holeD.closePath();
      shapeD.holes.push(holeD);

      const meshD = new THREE.Mesh(new THREE.ExtrudeGeometry(shapeD, extrudeSettings), logoMaterials.primary);
      meshD.position.x = 0.2;
      irdoTextGroup.add(meshD);

      // ==========================================
      // 4. "O" SEBAGAI APERTURE TARGET + CHIP
      // ==========================================
      const oGroup = new THREE.Group();
      oGroup.position.set(2.2, 0, 0);

      // Outer Target Ring
      const outerRingGeo = new THREE.TorusGeometry(1.45, 0.05, 16, 64);
      radarTargetRing = new THREE.Mesh(outerRingGeo, logoMaterials.primary);
      oGroup.add(radarTargetRing);

      // Crosshair Lines
      for (let i = 0; i < 4; i++) {
        const angle = (i / 4) * Math.PI * 2;
        const lineGeo = new THREE.BoxGeometry(2.9, 0.035, 0.08);
        const lineMesh = new THREE.Mesh(lineGeo, logoMaterials.primary);
        lineMesh.rotation.z = angle;
        oGroup.add(lineMesh);
      }

      // Microchip Core
      const chipGroup = new THREE.Group();
      const chipBase = new THREE.Mesh(
        new THREE.BoxGeometry(0.75, 0.75, 0.18),
        logoMaterials.darkBase
      );
      const chipDie = new THREE.Mesh(
        new THREE.BoxGeometry(0.35, 0.35, 0.22),
        logoMaterials.accent
      );
      chipGroup.add(chipBase, chipDie);

      // Chip Pins
      for (let i = 0; i < 4; i++) {
        const pin = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.05, 0.1), logoMaterials.accent);
        pin.position.set(-0.48, -0.22 + i * 0.15, 0);
        chipGroup.add(pin);

        const pin2 = pin.clone();
        pin2.position.x = 0.48;
        chipGroup.add(pin2);
      }
      chipCore = chipGroup;
      oGroup.add(chipCore);

      irdoTextGroup.add(oGroup);

      // ==========================================
      // 5. ANTENA / PIN SINYAL DI BAWAH "I" DAN "R"
      // ==========================================
      const createAntenna = (x, h) => {
        const group = new THREE.Group();
        const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.025, h, 12), logoMaterials.primary);
        pole.position.y = h / 2;
        const node = new THREE.Mesh(new THREE.SphereGeometry(0.065, 12, 12), logoMaterials.accent);
        node.position.y = h;
        group.add(pole, node);
        group.position.set(x, -1.2, 0.18);
        return group;
      };

      irdoTextGroup.add(createAntenna(-3.1, 0.5));
      irdoTextGroup.add(createAntenna(-2.9, 0.85));
      irdoTextGroup.add(createAntenna(-1.8, 0.85));
      irdoTextGroup.add(createAntenna(-1.6, 0.5));

      // ==========================================
      // 6. IKON SINYAL WIRELESS ((.)) KIRI ATAS
      // ==========================================
      const wirelessGroup = new THREE.Group();
      wirelessGroup.position.set(-3.1, 1.65, 0);

      const centerDot = new THREE.Mesh(new THREE.SphereGeometry(0.08, 12, 12), logoMaterials.accent);
      wirelessGroup.add(centerDot);

      const createArc = (radius) => {
        const arcGeo = new THREE.TorusGeometry(radius, 0.028, 8, 32, Math.PI / 1.5);
        const arcLeft = new THREE.Mesh(arcGeo, logoMaterials.primary);
        arcLeft.rotation.z = Math.PI / 1.5;

        const arcRight = new THREE.Mesh(arcGeo, arcLeft.geometry, logoMaterials.primary);
        arcRight.rotation.z = -Math.PI / 3;

        const arcGroup = new THREE.Group();
        arcGroup.add(arcLeft, arcRight);
        return arcGroup;
      };

      wirelessGroup.add(createArc(0.25));
      wirelessGroup.add(createArc(0.48));
      irdoTextGroup.add(wirelessGroup);

      logoGroup.add(irdoTextGroup);
    };

    // CINCIN ORBIT DUAL-AXIS (No Bottom Pane / Grid Floor)
    const buildOrbitalFX = (theme) => {
      // 1. Outer Orbit Ring 1 (Primary Wireframe Ring)
      const orbit1Geo = new THREE.TorusGeometry(4.2, 0.018, 16, 100);
      const orbit1Mat = new THREE.MeshBasicMaterial({
        color: theme.orbitRing1,
        wireframe: true,
        transparent: true,
        opacity: 0.35,
      });
      outerOrbitRing1 = new THREE.Mesh(orbit1Geo, orbit1Mat);
      outerOrbitRing1.rotation.x = Math.PI / 2.5;
      logoGroup.add(outerOrbitRing1);

      // 2. Outer Orbit Ring 2 (Accent Dashed Ring)
      const orbit2Geo = new THREE.TorusGeometry(3.6, 0.015, 12, 80);
      const orbit2Mat = new THREE.MeshBasicMaterial({
        color: theme.orbitRing2,
        wireframe: true,
        transparent: true,
        opacity: 0.3,
      });
      outerOrbitRing2 = new THREE.Mesh(orbit2Geo, orbit2Mat);
      outerOrbitRing2.rotation.y = Math.PI / 3;
      outerOrbitRing2.rotation.x = Math.PI / 4;
      logoGroup.add(outerOrbitRing2);
    };

    // Partikel Melayang di Seluruh Layar
    const buildGlobalParticles = (theme) => {
      const particleCount = 120;
      const particleGeometry = new THREE.BufferGeometry();
      const positions = new Float32Array(particleCount * 3);

      for (let i = 0; i < particleCount; i++) {
        positions[i * 3] = (Math.random() - 0.5) * 26;
        positions[i * 3 + 1] = (Math.random() - 0.5) * 14;
        positions[i * 3 + 2] = (Math.random() - 0.5) * 14;
      }

      particleGeometry.setAttribute(
        'position',
        new THREE.BufferAttribute(positions, 3)
      );

      particleMaterial = new THREE.PointsMaterial({
        color: theme.particle,
        size: 0.045,
        transparent: true,
        opacity: 0.55,
      });

      const particles = new THREE.Points(particleGeometry, particleMaterial);
      mainGroup.add(particles);
    };

    const handleMouseMove = (e) => {
      mouseX = (e.clientX / window.innerWidth - 0.5) * 2;
      mouseY = (e.clientY / window.innerHeight - 0.5) * 2;
    };

    const handleScroll = () => {
      scrollOffset = window.scrollY;
    };

    const handleResize = () => {
      if (!container) return;
      const width = container.clientWidth;
      const height = container.clientHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };

    const animate = () => {
      animationId = requestAnimationFrame(animate);

      // Smooth Rotation
      logoGroup.rotation.y = Math.sin(Date.now() * 0.0008) * 0.18;
      logoGroup.rotation.x = Math.cos(Date.now() * 0.0006) * 0.06;

      if (radarTargetRing) {
        radarTargetRing.rotation.z += 0.006;
      }

      if (chipCore) {
        chipCore.rotation.z -= 0.008;
      }

      if (outerOrbitRing1) {
        outerOrbitRing1.rotation.z += 0.003;
        outerOrbitRing1.rotation.y += 0.002;
      }

      if (outerOrbitRing2) {
        outerOrbitRing2.rotation.z -= 0.004;
        outerOrbitRing2.rotation.x += 0.002;
      }

      // Parallax Mouse Interaction
      targetRotationX = mouseY * 0.12;
      targetRotationY = mouseX * 0.18;

      mainGroup.rotation.x += (targetRotationX - mainGroup.rotation.x) * 0.02;
      mainGroup.rotation.y += (targetRotationY - mainGroup.rotation.y) * 0.02;

      const scrollReaction = Math.min(scrollOffset * 0.0001, 0.3);
      camera.position.z = 11 - scrollReaction;

      renderer.render(scene, camera);
    };

    const cleanupThemeObserver = init();
    animate();

    return () => {
      cancelAnimationFrame(animationId);
      if (cleanupThemeObserver) cleanupThemeObserver();
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);

      scene.traverse((object) => {
        if (object.geometry) object.geometry.dispose();
        if (object.material) {
          if (Array.isArray(object.material)) {
            object.material.forEach((m) => m.dispose());
          } else {
            object.material.dispose();
          }
        }
      });

      if (renderer) {
        renderer.dispose();
        if (renderer.domElement && renderer.domElement.parentNode) {
          renderer.domElement.parentNode.removeChild(renderer.domElement);
        }
      }
    };
  }, [isMobile, prefersReducedMotion]);

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 pointer-events-none"
      aria-hidden="true"
    />
  );
};

export default HeroScene;